<template>
  <div class="personal-additional-info">
    <van-nav-bar
      :fixed="true"
      z-index="9"
      :title="$t('personalAdditionalInfo.title')"
      :border="false"
      left-arrow
      @click-left="onClickLeft"
    />

    <div class="intro-text" :class="`intro-text--${bannerTone}`">
      <div class="intro-text__icon"><van-icon name="info-o" /></div>
      <div>
        <strong v-if="bannerTitle">{{ bannerTitle }}</strong>
        <span>{{ bannerDescription }}</span>
      </div>
    </div>

    <div class="section-title">1. {{ $t('personalAdditionalInfo.basicVerification') }}</div>
    <div class="verification-card">
      <div class="verification-card__icon"><van-icon name="idcard" /></div>
      <div class="verification-card__body">
        <div class="verification-card__title">
          {{ $t('personalAdditionalInfo.identityVerification') }}
          <span class="required-mark">*</span>
        </div>
        <div class="verification-card__desc">
          <template v-if="basicVerificationState === 'ocr_failed' || basicVerificationState === 'face_failed'">
            <div>{{ $t(`personalAdditionalInfo.basic.${basicVerificationState}.summary`) }}</div>
            <div class="verification-card__failure-desc">{{ $t(`personalAdditionalInfo.basic.${basicVerificationState}.desc`) }}</div>
          </template>
          <template v-else>{{ basicVerificationDesc }}</template>
        </div>
      </div>
      <div class="verification-card__status">
        <span v-if="showBasicStatus" class="status-badge" :class="`status-badge--${basicStatusState}`">{{ basicStatusLabel }}</span>
        <div
          v-if="!basicVerified && !memberInfoLoading"
          class="verification-card__action"
          @click="startKyc"
        >
          {{ $t('personalAdditionalInfo.verify') }}
        </div>
      </div>
    </div>

    <div class="section-title section-title--with-status">
      <span>2. {{ $t('personalAdditionalInfo.additionalDocuments') }}</span>
    </div>
    <div v-if="showDocumentTip" class="document-tip" :class="{ 'document-tip--rejected': displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.RESUBMIT }">
      <div class="document-tip__icon"><van-icon name="info-o" /></div>
      <div>{{ documentTipText }}</div>
    </div>
    <div v-if="additionalInfoLoading" class="state-card">{{ $t('loading') }}</div>
    <div v-else-if="additionalInfoLoadError" class="state-card state-card--error">
      <span>{{ $t('personalAdditionalInfo.loadFailed') }}</span>
      <button type="button" @click="loadAdditionalInfo">{{ $t('personalAdditionalInfo.retry') }}</button>
    </div>
    <div v-else class="card documents-card">
      <div
        v-for="(doc, index) in documents"
        :key="doc.type"
        class="doc-item"
        :class="{ 'is-first': index === 0, 'is-disabled': !canEditDocument(doc) && documentStatusState(doc) === 'not-submitted' }"
        @click="canEditDocument(doc) && openUploader(doc.type)"
      >
        <div class="doc-item__icon">
          <img v-if="doc.uploaded && doc.fileUrl" :src="doc.fileUrl" alt="" />
          <img v-else :src="getDocIcon(doc.type)" alt="" />
        </div>
        <div class="doc-item__name">
          {{ getDocName(doc.type) }}
          <span v-if="isRequiredDocument(doc.type)" class="required-mark">*</span>
          <div class="doc-item__desc">{{ documentDescription(doc) }}</div>
        </div>
        <div class="doc-item__status">
          <span v-if="uploadingType === doc.type" class="uploading-label">{{ $t('personalAdditionalInfo.uploading') }}</span>
          <span v-else-if="doc.uploaded || isRejectedDocument(doc)" class="status-badge" :class="`status-badge--${documentStatusState(doc)}`">
            {{ documentStatusLabel(doc) }}
          </span>
          <span v-if="uploadingType !== doc.type && canEditDocument(doc) && (!doc.uploaded || isRejectedDocument(doc))" class="upload-action">{{ documentActionLabel(doc) }}</span>
        </div>
      </div>
      <input
        ref="fileInput"
        class="file-input"
        type="file"
        :accept="[DOC_TYPE.BANK_STATEMENT, DOC_TYPE.PAYSLIP].includes(selectedUploadType) ? '.jpg,.jpeg,.png,.pdf' : '.jpg,.jpeg,.png'"
        @change="afterRead"
      />
    </div>

    <div v-if="displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.READY_TO_SUBMIT" class="document-tip review-tip">
      <div class="document-tip__icon"><van-icon name="info-o" /></div>
      <div>{{ $t('personalAdditionalInfo.reviewBeforeSubmit') }}</div>
    </div>

    <div v-if="showPostReviewCards" class="post-review">
      <div class="post-review__card">
        <div class="post-review__title">{{ postReviewTitle }}</div>
        <div class="post-review__desc">{{ postReviewDescription }}</div>
      </div>
      <div class="post-review__card post-review__card--action" @click="goCustomerService">
        <div>
          <div class="post-review__title">{{ $t('personalAdditionalInfo.needHelpTitle') }}</div>
          <div class="post-review__desc">{{ $t('personalAdditionalInfo.needHelpDesc') }}</div>
        </div>
        <span>{{ $t('customerService') }}</span>
      </div>
    </div>

    <div class="personal-additional-info__footer">
      <template v-if="showSubmitControls">
        <div class="agreement">
          <van-checkbox v-model="termsAgreed" :disabled="!additionalEditable">
            {{ $t('additionalInfo.terms.agreeText') }}
            <span v-for="(ag, index) in agreements" :key="index" class="privacy-link" @click.stop="goToAgreement(ag)">
              {{ ag.title }}{{ index < agreements.length - 1 ? ', ' : '' }}
            </span>
          </van-checkbox>
        </div>
        <button type="button" class="btn-primary submit-button" :class="{ 'btn-disabled': !canSubmit }" :disabled="!canSubmit" @click="onSubmit">
          {{ submitting ? $t('loading') : $t('personalAdditionalInfo.confirmSubmit') }}
        </button>
      </template>
      <div class="privacy-protection">
        <van-icon name="shield-o" />
        <span>{{ $t('personalAdditionalInfo.protectedText') }}</span>
      </div>
    </div>

    <van-dialog
      v-model="showCustomerService"
      class="customer-service-dialog"
      :show-confirm-button="false"
    >
      <div class="customer-service">
        <div class="customer-service__close" @click="closeCustomerService">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="customer-service__title">{{ $t('customerService') }}</div>
        <div class="customer-service__content">
          <div class="customer-service__row">
            <span>{{ $t('customerServicePop.whatsapp') }}:</span>
            <strong>{{ customerServicePhone }}</strong>
          </div>
        </div>
        <div class="customer-service__footer">
          <div class="btn-primary customer-service__copy" @click="copyCustomerServicePhone">
            {{ $t('customerServicePop.copy') }}
          </div>
        </div>
      </div>
    </van-dialog>

    <van-dialog
      v-model="showSubmitSuccess"
      :title="$t('personalAdditionalInfo.submissionSuccessTitle')"
      :message="$t('personalAdditionalInfo.submissionSuccessDesc')"
      :confirm-button-text="$t('confirm')"
      @confirm="handleSubmitSuccessConfirm"
    />

    <KycPopup ref="kycPopup" />
    <AuthResultPopup
      ref="authResultPopup"
      :auto-continue="false"
      :success-button-text="$t('confirm')"
      @success="handleAuthResultSuccess"
      @retry="handleAuthResultRetry"
      @back="handleAuthResultBack"
      @close="handleAuthResultClose"
    />
    <div v-if="kycRedirectLoading" class="kyc-redirect-loading">
      <van-loading type="spinner" color="#fff" vertical>
        {{ $t('personalAdditionalInfo.verifying') }}
      </van-loading>
    </div>
  </div>
</template>

<script>
import { Checkbox, Dialog, Icon, Loading } from 'vant'
import { mapState } from 'vuex'
import axios from 'axios'
import agreement from '@/mixins/agreement'
import {
  DOC_TYPE,
  getUploadUrl,
  queryAdditionalInfo,
  submitAdditionalInfo,
  SupplementStatus
} from '@/api/additionalInfo'
import { getAgreements, getAppSystemConfig } from '@/api/common'
import { getAuthResult } from '@/api/kyc'
import {
  clearFinishedKycV2,
  completeKycV2Flow,
  retryKycV2Flow,
  startKycV2Flow
} from '@/utils/kycV2Flow'
import { hasKycV2Context, isKycV2Return } from '@/utils/kycV2'
import { getMemberInfo } from '@/api/member'
import { AGREEMENT_SCENE } from '@/common/constants'
import KycPopup from '@/components/KycPopup/index.vue'
import AuthResultPopup from '@/components/AuthResultPopup/index.vue'
import { copyText } from '@/utils/clipboard'
import { DEFAULT_REG_CHANNEL } from '@/config'
import {
  buildSupplementSubmitItems,
  hasNewSupplementUpload,
  hasRequiredSupplementUploads,
  hasValidBpjsTkPair,
  hasValidDriverLicensePair,
  isBpjsTkType,
  isSupplementApproved,
  isSupplementEditableStatus,
  isSupplementResubmit,
  isSupplementSubmitted,
  normalizeSupplementDocuments,
  normalizeSupplementStatus,
  PERSONAL_ADDITIONAL_DISPLAY_STATE,
  resolvePersonalAdditionalDisplayState,
  resolveRequiredSupplementTypes
} from '@/common/supplement'

const PERSONAL_KYC_TOKEN_KEY = 'personalAdditionalKycAuthToken'
const PAGE_HISTORY_KEY = 'pageHistory'

export default {
  name: 'PersonalAdditionalInfo',
  mixins: [agreement],
  components: {
    'van-checkbox': Checkbox,
    'van-dialog': Dialog.Component,
    'van-icon': Icon,
    'van-loading': Loading,
    KycPopup,
    AuthResultPopup
  },
  data() {
    return {
      memberInfo: null,
      documents: normalizeSupplementDocuments([]),
      requiredDocumentTypes: [DOC_TYPE.FAMILY_CARD],
      overallStatus: SupplementStatus.UNSUBMIT,
      agreements: [],
      termsAgreed: false,
      submitting: false,
      showSubmitSuccess: false,
      showCustomerService: false,
      customerServicePhone: '+62 822-2777-6752',
      selectedUploadType: '',
      uploadingType: '',
      memberInfoLoadState: 'loading',
      additionalInfoLoadState: 'loading',
      handlingKycRedirect: false,
      memberInfoRequestSeq: 0,
      kycRedirectLoading: false
    }
  },
  computed: {
    ...mapState('user', ['channel']),
    DOC_TYPE() {
      return DOC_TYPE
    },
    PERSONAL_ADDITIONAL_DISPLAY_STATE() {
      return PERSONAL_ADDITIONAL_DISPLAY_STATE
    },
    memberInfoLoading() {
      return this.memberInfoLoadState === 'loading'
    },
    additionalInfoLoading() {
      return this.additionalInfoLoadState === 'loading'
    },
    additionalInfoLoadError() {
      return this.additionalInfoLoadState === 'error'
    },
    basicVerified() {
      const info = this.memberInfo || {}
      return `${info.kyc || ''}`.toUpperCase() === 'SUCCESS' && `${info.ocr || ''}`.toUpperCase() === 'SUCCESS'
    },
    basicVerificationState() {
      if (this.memberInfoLoading) return 'loading'
      if (this.memberInfoLoadState === 'error' && !this.memberInfo) return 'error'
      if (this.basicVerified) return 'verified'
      const info = this.memberInfo || {}
      const kyc = `${info.kyc || ''}`.toUpperCase()
      const ocr = `${info.ocr || ''}`.toUpperCase()
      if (ocr === 'FAILED') return 'ocr_failed'
      if (kyc === 'FAILED') return 'face_failed'
      if (kyc === 'PENDING' && ocr === 'PENDING') return 'pending'
      return 'unverified'
    },
    basicVerificationDesc() {
      return this.$t(`personalAdditionalInfo.basic.${this.basicVerificationState}.desc`)
    },
    basicStatusState() {
      if (this.basicVerificationState === 'loading') return 'loading'
      if (this.basicVerificationState === 'verified') return 'approved'
      return 'not-submitted'
    },
    basicStatusLabel() {
      if (this.basicVerificationState === 'loading') return this.$t('loading')
      if (this.basicVerificationState !== 'verified') return this.$t('personalAdditionalInfo.notVerified')
      return this.$t(`additionalInfo.status.${this.basicStatusState}`)
    },
    showBasicStatus() {
      return this.basicVerificationState !== 'pending'
    },
    displayState() {
      return resolvePersonalAdditionalDisplayState(this.overallStatus, this.documents, this.basicVerified)
    },
    additionalEditable() {
      return this.basicVerified &&
        this.additionalInfoLoadState === 'success' &&
        this.displayState !== PERSONAL_ADDITIONAL_DISPLAY_STATE.UNDER_REVIEW &&
        this.displayState !== PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED
    },
    showSubmitControls() {
      return this.additionalInfoLoadState === 'success' && (
        !this.basicVerified ||
        this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.EDITING ||
        this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.READY_TO_SUBMIT
      )
    },
    canSubmit() {
      return this.showSubmitControls &&
        this.additionalEditable &&
        !this.submitting &&
        !this.uploadingType &&
        this.termsAgreed &&
        hasRequiredSupplementUploads(this.documents, this.requiredDocumentTypes) &&
        hasNewSupplementUpload(this.documents) &&
        hasValidDriverLicensePair(this.documents) &&
        hasValidBpjsTkPair(this.documents)
    },
    bannerTone() {
      if (this.isBasicVerificationFailed || this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.RESUBMIT) return 'rejected'
      if (this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED) return 'approved'
      if (this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.READY_TO_SUBMIT) return 'ready'
      return 'default'
    },
    bannerTitle() {
      if (this.bannerTone === 'rejected') return this.$t('personalAdditionalInfo.rejectedTitle')
      if (this.bannerTone === 'approved') return this.$t('personalAdditionalInfo.approvedTitle')
      if (this.bannerTone === 'ready') return this.$t('personalAdditionalInfo.readyTitle')
      return ''
    },
    bannerDescription() {
      if (this.basicVerificationState === 'ocr_failed') return this.$t('personalAdditionalInfo.basic.ocr_failed.bannerDesc')
      if (this.basicVerificationState === 'face_failed') return this.$t('personalAdditionalInfo.basic.face_failed.bannerDesc')
      if (this.bannerTone === 'rejected') return this.$t('personalAdditionalInfo.rejectedDesc')
      if (this.bannerTone === 'approved') return this.$t('personalAdditionalInfo.approvedDesc')
      if (this.bannerTone === 'ready') return this.$t('personalAdditionalInfo.readyDesc')
      return this.$t('personalAdditionalInfo.intro')
    },
    showDocumentTip() {
      return this.displayState !== PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED
    },
    documentTipText() {
      if (this.overallStatus === SupplementStatus.SUBMITTED || this.documents.some(doc => isSupplementSubmitted(doc.status))) return this.$t('personalAdditionalInfo.underReviewTip')
      if (this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.READY_TO_SUBMIT) return this.$t('personalAdditionalInfo.readyDocumentsTip')
      if (this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.RESUBMIT) return this.$t('personalAdditionalInfo.rejectedDocumentsTip')
      return this.$t('personalAdditionalInfo.documentsTip')
    },
    showPostReviewCards() {
      return this.isBasicVerificationFailed ||
        [PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED, PERSONAL_ADDITIONAL_DISPLAY_STATE.RESUBMIT].includes(this.displayState)
    },
    isBasicVerificationFailed() {
      return ['ocr_failed', 'face_failed'].includes(this.basicVerificationState)
    },
    postReviewTitle() {
      if (this.basicVerificationState === 'ocr_failed') return this.$t('personalAdditionalInfo.basic.ocr_failed.nextTitle')
      if (this.basicVerificationState === 'face_failed') return this.$t('personalAdditionalInfo.basic.face_failed.nextTitle')
      return this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED
        ? this.$t('personalAdditionalInfo.whatNextTitle')
        : this.$t('personalAdditionalInfo.avoidRejectionTitle')
    },
    postReviewDescription() {
      if (this.basicVerificationState === 'ocr_failed') return this.$t('personalAdditionalInfo.basic.ocr_failed.nextDesc')
      if (this.basicVerificationState === 'face_failed') return this.$t('personalAdditionalInfo.basic.face_failed.nextDesc')
      return this.displayState === PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED
        ? this.$t('personalAdditionalInfo.whatNextDesc')
        : this.$t('personalAdditionalInfo.avoidRejectionDesc')
    }
  },
  created() {
    const isV2Return = isKycV2Return(this.$route)
    if (isV2Return) this.showKycRedirectLoading()
    this.loadPageData(isV2Return ? { _showLoading: false } : {})
  },
  mounted() {
    this.handleKycRedirect()
  },
  watch: {
    '$route.fullPath'() {
      this.$nextTick(() => {
        this.handleKycRedirect()
      })
    }
  },
  methods: {
    isBpjsTkType,
    onClickLeft() {
      const fallbackPath = '/profile/index'
      let history = []
      try {
        history = JSON.parse(sessionStorage.getItem(PAGE_HISTORY_KEY) || '[]')
      } catch (e) {
        history = []
      }
      const safeHistory = history.filter(path => {
        const pathOnly = `${path || ''}`.split('?')[0]
        return pathOnly &&
          pathOnly !== this.$route.path &&
          !pathOnly.startsWith('/kyc/')
      })
      const target = safeHistory.pop() || fallbackPath
      sessionStorage.setItem(PAGE_HISTORY_KEY, JSON.stringify(safeHistory))
      sessionStorage.setItem('isGoBack', 'true')
      this.$router.replace(target)
    },
    documentStatusState(doc) {
      if (isSupplementApproved(doc?.status)) return 'approved'
      if (isSupplementSubmitted(doc?.status)) return 'submitted'
      if (isSupplementResubmit(doc?.status)) return 'not-submitted'
      if (doc?.uploaded && doc?.ossKey) return 'uploaded'
      return 'not-submitted'
    },
    documentStatusLabel(doc) {
      if (this.documentStatusState(doc) === 'uploaded') {
        return this.$t('additionalInfo.upload.uploaded')
      }
      return this.$t(`additionalInfo.status.${this.documentStatusState(doc)}`)
    },
    isRejectedDocument(doc) {
      return isSupplementResubmit(doc?.status)
    },
    documentDescription(doc) {
      if (isSupplementResubmit(doc?.status)) {
        return doc.rejectReason || doc.reason || doc.remark || this.$t('personalAdditionalInfo.rejectedDocumentDesc')
      }
      if (isSupplementApproved(doc?.status)) {
        return this.getVerifiedDocDescription(doc.type)
      }
      return doc?.uploaded
        ? (doc.type === DOC_TYPE.BANK_STATEMENT
          ? this.$t('additionalInfo.upload.uploadedBankStatement')
          : this.$t('additionalInfo.upload.uploaded'))
        : this.getDocDescription(doc.type)
    },
    documentActionLabel(doc) {
      return isSupplementResubmit(doc?.status)
        ? this.$t('personalAdditionalInfo.reupload')
        : this.$t('personalAdditionalInfo.upload')
    },
    async loadPageData(config = {}) {
      await Promise.all([
        this.loadMemberInfo(config),
        this.loadAdditionalInfo(config),
        this.loadRequiredDocumentTypes(),
        this.queryAgreements(config)
      ])
    },
    async loadMemberInfo(config = {}) {
      const requestSeq = this.memberInfoRequestSeq + 1
      this.memberInfoRequestSeq = requestSeq
      if (!this.memberInfo) this.memberInfoLoadState = 'loading'
      try {
        const res = await getMemberInfo(config)
        if (res.code !== 'Success') throw new Error(res.message || '')
        const memberInfo = res.data || {}
        if (requestSeq !== this.memberInfoRequestSeq) return null
        this.memberInfo = memberInfo
        this.memberInfoLoadState = 'success'
        return memberInfo
      } catch (e) {
        if (requestSeq !== this.memberInfoRequestSeq) return null
        if (!this.memberInfo) this.memberInfoLoadState = 'error'
        return null
      }
    },
    async loadAdditionalInfo(config = {}) {
      if (this.additionalInfoLoadState !== 'success') this.additionalInfoLoadState = 'loading'
      try {
        const res = await queryAdditionalInfo(config)
        if (res.code !== 'Success') throw new Error(res.message || '')
        const statusData = res.data || {}
        this.overallStatus = normalizeSupplementStatus(statusData.overallStatus)
        this.documents = normalizeSupplementDocuments(statusData.items || statusData.documents || [])
        this.additionalInfoLoadState = 'success'
      } catch (e) {
        if (this.additionalInfoLoadState !== 'success') this.additionalInfoLoadState = 'error'
      }
    },
    async loadRequiredDocumentTypes() {
      try {
        const res = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL })
        const features = res?.data?.features || res?.data || {}
        this.requiredDocumentTypes = resolveRequiredSupplementTypes(features)
      } catch (e) {
        // 配置失败时保留 Android 同步的 FAMILY_CARD 兜底。
        this.requiredDocumentTypes = resolveRequiredSupplementTypes()
      }
    },
    async queryAgreements(config = {}) {
      try {
        const res = await getAgreements({ sceneCode: AGREEMENT_SCENE.ADDITIONAL_INFO }, config)
        if (res.code === 'Success') this.agreements = res.data.agreements || []
      } catch (e) {
        // 协议接口失败不清空已经成功加载的协议。
      }
    },
    isRequiredDocument(type) {
      return this.requiredDocumentTypes.some(item => `${item}`.toUpperCase() === `${type}`.toUpperCase())
    },
    canEditDocument(doc) {
      return this.additionalEditable &&
        !this.submitting &&
        !this.uploadingType &&
        isSupplementEditableStatus(doc?.status)
    },
    getDocName(type) {
      const map = {
        [DOC_TYPE.FAMILY_CARD]: 'additionalInfo.docs.familyCard',
        [DOC_TYPE.SOCIAL_MEDIA]: 'additionalInfo.docs.socialMedia',
        [DOC_TYPE.BANK_STATEMENT]: 'additionalInfo.docs.bankStatement',
        [DOC_TYPE.PAYSLIP]: 'additionalInfo.docs.payslip',
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'additionalInfo.docs.driverLicenseFront',
        [DOC_TYPE.DRIVER_LICENSE_BACK]: 'additionalInfo.docs.driverLicenseBack',
        [DOC_TYPE.BPJS_TK_FRONT]: 'additionalInfo.docs.bpjsTkFront',
        [DOC_TYPE.BPJS_TK_BACK]: 'additionalInfo.docs.bpjsTkBack'
      }
      return this.$t(map[type] || type)
    },
    getDocDescription(type) {
      const map = {
        [DOC_TYPE.FAMILY_CARD]: 'personalAdditionalInfo.docs.familyCardDesc',
        [DOC_TYPE.SOCIAL_MEDIA]: 'personalAdditionalInfo.docs.socialMediaDesc',
        [DOC_TYPE.BANK_STATEMENT]: 'personalAdditionalInfo.docs.bankStatementDesc',
        [DOC_TYPE.PAYSLIP]: 'personalAdditionalInfo.docs.payslipDesc',
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'personalAdditionalInfo.docs.driverLicenseFrontDesc',
        [DOC_TYPE.DRIVER_LICENSE_BACK]: 'personalAdditionalInfo.docs.driverLicenseBackDesc',
        [DOC_TYPE.BPJS_TK_FRONT]: 'personalAdditionalInfo.docs.bpjsTkDesc',
        [DOC_TYPE.BPJS_TK_BACK]: 'personalAdditionalInfo.docs.bpjsTkDesc'
      }
      return this.$t(map[type] || 'personalAdditionalInfo.documentsDesc')
    },
    getVerifiedDocDescription(type) {
      const map = {
        [DOC_TYPE.FAMILY_CARD]: 'personalAdditionalInfo.docs.familyCardVerifiedDesc',
        [DOC_TYPE.SOCIAL_MEDIA]: 'personalAdditionalInfo.docs.socialMediaVerifiedDesc',
        [DOC_TYPE.BANK_STATEMENT]: 'personalAdditionalInfo.docs.bankStatementVerifiedDesc',
        [DOC_TYPE.PAYSLIP]: 'personalAdditionalInfo.docs.payslipVerifiedDesc',
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'personalAdditionalInfo.docs.driverLicenseVerifiedDesc',
        [DOC_TYPE.DRIVER_LICENSE_BACK]: 'personalAdditionalInfo.docs.driverLicenseVerifiedDesc',
        [DOC_TYPE.BPJS_TK_FRONT]: 'personalAdditionalInfo.docs.bpjsTkVerifiedDesc',
        [DOC_TYPE.BPJS_TK_BACK]: 'personalAdditionalInfo.docs.bpjsTkVerifiedDesc'
      }
      return this.$t(map[type] || 'additionalInfo.upload.uploaded')
    },
    getDocIcon(type) {
      const map = {
        [DOC_TYPE.FAMILY_CARD]: require('@/assets/img/additional-info/family_card_icon.png'),
        [DOC_TYPE.SOCIAL_MEDIA]: require('@/assets/img/additional-info/social_media_icon.png'),
        [DOC_TYPE.BANK_STATEMENT]: require('@/assets/img/additional-info/bank_statement_icon.png'),
        [DOC_TYPE.PAYSLIP]: require('@/assets/img/additional-info/payslip_icon.png'),
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.DRIVER_LICENSE_BACK]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.BPJS_TK_FRONT]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.BPJS_TK_BACK]: require('@/assets/img/additional-info/drivers_license_icon.png')
      }
      return map[type] || map[DOC_TYPE.FAMILY_CARD]
    },
    async openUploader(type) {
      const document = this.documents.find(item => item.type === type)
      if (!document || !this.canEditDocument(document)) return
      this.selectedUploadType = type
      await this.$nextTick()
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },
    async afterRead(event) {
      const file = event.target.files[0]
      const type = this.selectedUploadType
      const target = this.documents.find(document => document.type === type)
      if (!file || !type || !target || !this.canEditDocument(target)) return
      const allowsPdf = [DOC_TYPE.BANK_STATEMENT, DOC_TYPE.PAYSLIP].includes(type)
      const allowedExtensions = allowsPdf ? /\.(jpe?g|png|pdf)$/i : /\.(jpe?g|png)$/i
      if (!allowedExtensions.test(file.name)) {
        return this.$toast(this.$t('additionalInfo.fileTypeTip', { formats: allowsPdf ? 'JPG, JPEG, PNG, PDF' : 'JPG, JPEG, PNG' }))
      }
      const maxSizeMB = /\.pdf$/i.test(file.name) ? 10 : 2
      const maxSize = maxSizeMB * 1024 * 1024
      if (file.size > maxSize) {
        this.$toast(this.$t('additionalInfo.upload.fileTooLarge', { size: maxSizeMB }))
        return
      }
      this.uploadingType = type
      try {
        const resp = await getUploadUrl({ type, fileName: file.name })
        if (resp.code !== 'Success' || !resp.data) throw new Error(resp.message || '')
        const data = resp.data
        const formData = new FormData()
        formData.append('policy', data.policy)
        formData.append('OSSAccessKeyId', data.OSSAccessKeyId)
        formData.append('signature', data.signature)
        formData.append('key', data.key)
        formData.append('Content-Type', data.contentType)
        formData.append('success_action_status', '200')
        formData.append('file', file)
        await axios.post(data.host, formData)
        this.$set(target, 'uploaded', true)
        this.$set(target, 'ossKey', data.key)
        this.$set(target, 'status', SupplementStatus.UNSUBMIT)
      } catch (e) {
        this.$toast(this.$t('additionalInfo.upload.failed'))
      } finally {
        this.uploadingType = ''
      }
    },
    async onSubmit() {
      if (!this.canSubmit) return
      if (!this.termsAgreed) return this.$toast(this.$t('additionalInfo.terms.notAgreedTip'))
      if (!hasRequiredSupplementUploads(this.documents, this.requiredDocumentTypes)) {
        return this.$toast(this.$t('additionalInfo.requiredDocumentsTip'))
      }
      if (!hasNewSupplementUpload(this.documents)) {
        return this.$toast(this.$t('additionalInfo.uploadAtLeastOneTip'))
      }
      if (!hasValidDriverLicensePair(this.documents)) {
        return this.$toast(this.$t('additionalInfo.driverLicensePairTip'))
      }
      if (!hasValidBpjsTkPair(this.documents)) {
        return this.$toast(this.$t('additionalInfo.bpjsTkPairTip'))
      }
      this.submitting = true
      try {
        const res = await submitAdditionalInfo({ items: buildSupplementSubmitItems(this.documents) })
        if (res.code === 'Success') {
          this.showSubmitSuccess = true
        } else {
          this.$toast(res.message || this.$t('additionalInfo.submitFailed'))
        }
      } catch (e) {
        this.$toast(this.$t('additionalInfo.submitFailed'))
      } finally {
        this.submitting = false
      }
    },
    handleSubmitSuccessConfirm() {
      this.termsAgreed = false
      this.loadPageData()
    },
    goCustomerService() {
      this.showCustomerService = true
    },
    closeCustomerService() {
      this.showCustomerService = false
    },
    async copyCustomerServicePhone() {
      const success = await copyText(this.customerServicePhone)
      this.$toast(this.$t(success ? 'customerServicePop.copySuccess' : 'customerServicePop.copyFailed'))
    },
    getKycRedirectToken() {
      const { token, bizToken, biz_token } = this.$route.query
      return token || bizToken || biz_token || sessionStorage.getItem(PERSONAL_KYC_TOKEN_KEY)
    },
    clearAuthResultQuery() {
      const { authType, token, bizToken, biz_token, ...query } = this.$route.query
      if (authType || token || bizToken || biz_token) this.$router.replace({ path: this.$route.path, query })
    },
    async handleKycRedirect() {
      const isV2Return = isKycV2Return(this.$route)
      const authType = this.$route.query.authType
      if (!isV2Return && !authType) return
      if (this.handlingKycRedirect) return
      this.handlingKycRedirect = true

      if (isV2Return) {
        let returnView = null
        this.showKycRedirectLoading()
        try {
          returnView = this.resolveKycReturnView()
          const view = await completeKycV2Flow(this, { silent: !!returnView, throwOnError: true })
          const popupOpened = await this.refreshMemberInfoAndOpenAuthResult()
          if (view && !popupOpened) {
            clearFinishedKycV2(this)
          }
        } catch (e) {
          if (returnView) {
            this.$toast(this.$t('systemError'))
            this.loadMemberInfo({ _showLoading: false })
          } else {
            this.$toast(this.$t('systemError'))
            clearFinishedKycV2(this)
          }
        } finally {
          this.hideKycRedirectLoading()
          this.handlingKycRedirect = false
        }
        return
      }
      const token = this.getKycRedirectToken()
      if (!token) {
        this.clearAuthResultQuery()
        this.handlingKycRedirect = false
        return
      }
      try {
        const res = await getAuthResult({ token, type: authType })
        if (res.code === 'Success' && res.data?.verifyResult) {
          this.$refs.authResultPopup.open({ authType: res.data.type || authType, verifyResult: res.data.verifyResult })
        } else {
          this.clearAuthResultQuery()
        }
      } catch (e) {
        this.$toast(this.$t('systemError'))
        this.clearAuthResultQuery()
      } finally {
        this.handlingKycRedirect = false
      }
    },
    resolveKycReturnView() {
      const state = `${this.$route.query.kycState || ''}`.toLowerCase()
      if (state === 'success') {
        return {
          status: 'SUCCESS',
          verifyResult: 'SUCCESS'
        }
      }
      if (['fail', 'failed', 'cancel', 'cancelled'].includes(state)) {
        return {
          status: 'FAILED',
          verifyResult: 'FAIL'
        }
      }
      return null
    },
    showKycRedirectLoading() {
      this.kycRedirectLoading = true
    },
    hideKycRedirectLoading() {
      this.kycRedirectLoading = false
    },
    async refreshMemberInfoAndOpenAuthResult(options = {}) {
      const { allowSuccess = true, allowFailure = true } = options
      const memberInfo = await this.loadMemberInfo({ _showLoading: false })
      const authResult = this.resolveMemberInfoAuthResult(memberInfo)
      if (!authResult) return false
      if (authResult.verifyResult === 'SUCCESS' && !allowSuccess) return false
      if (authResult.verifyResult !== 'SUCCESS' && !allowFailure) return false
      this.hideKycRedirectLoading()
      this.$refs.authResultPopup.open(authResult)
      return true
    },
    resolveMemberInfoAuthResult(memberInfo = {}) {
      const kyc = `${memberInfo?.kyc || ''}`.toUpperCase()
      const ocr = `${memberInfo?.ocr || ''}`.toUpperCase()
      if (kyc === 'SUCCESS' && ocr === 'SUCCESS') {
        return {
          authType: 'ocr',
          verifyResult: 'SUCCESS'
        }
      }
      if (ocr === 'FAILED') {
        return {
          authType: 'ocr',
          verifyResult: 'FAIL'
        }
      }
      if (kyc === 'FAILED') {
        return {
          authType: 'face',
          verifyResult: 'FAIL'
        }
      }
      return null
    },
    async startKyc() {
      try {
        const ready = await startKycV2Flow(this, {
          businessScene: 'personalAdditionalInfo',
          returnPath: '/personal-additional-info'
        })
        if (ready) {
          await this.loadMemberInfo()
        }
      } catch (e) {
        this.$toast(this.$t('systemError'))
      }
    },
    handleAuthResultSuccess() {
      sessionStorage.removeItem(PERSONAL_KYC_TOKEN_KEY)
      clearFinishedKycV2(this)
      this.clearAuthResultQuery()
      this.loadMemberInfo({ _showLoading: false })
    },
    handleAuthResultClose() {
      sessionStorage.removeItem(PERSONAL_KYC_TOKEN_KEY)
      clearFinishedKycV2(this)
      this.clearAuthResultQuery()
      this.loadMemberInfo({ _showLoading: false })
    },
    async handleAuthResultRetry() {
      this.clearAuthResultQuery()
      if (hasKycV2Context()) {
        await retryKycV2Flow(this)
        return
      }
      await this.startKyc()
    },
    handleAuthResultBack() {
      sessionStorage.removeItem(PERSONAL_KYC_TOKEN_KEY)
      clearFinishedKycV2(this)
      this.clearAuthResultQuery()
    }
  }
}
</script>

<style lang="less" scoped>
.personal-additional-info {
  min-height: 100vh;
  padding: 112px 24px 32px;
  background: var(--bg-color);
  box-sizing: border-box;

  .intro-card,
  .verification-card,
  .card {
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .06);
  }

  .intro-card {
    display: flex;
    align-items: flex-start;
    padding: 28px;
    margin-bottom: 24px;
    box-shadow: none;
    color: #536B8A;
  }

  .intro-text {
    display: flex;
    align-items: flex-start;
    padding: 4px 4px 8px;
    color: #536B8A;
    font-size: 26px;
    line-height: 38px;
  }

  .intro-text__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-right: 18px;
    border-radius: 50%;
    background: #EAF1FF;
    color: var(--primary-color);
    font-size: 30px;
    flex-shrink: 0;
  }

  .intro-text strong {
    display: block;
    margin-bottom: 6px;
    color: var(--text-color-black);
  }

  .intro-text--ready {
    padding: 20px 24px;
    border-radius: 16px;
    background: #EAF1FF;
  }

  .intro-text--approved {
    padding: 20px 24px;
    border-radius: 16px;
    background: #E5F8ED;
  }

  .intro-text--approved .intro-text__icon {
    background: #D9F4E4;
    color: #008A3E;
  }

  .intro-card__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    border-radius: 16px;
    background: #EAF1FF;
    color: var(--primary-color);
    font-size: 34px;
  }

  .intro-card__text {
    margin-left: 20px;
    font-size: 26px;
    line-height: 38px;
  }

  .section-title {
    margin: 24px 4px 16px;
    font-size: 32px;
    line-height: 44px;
    font-weight: 600;
    color: var(--text-color-black);
  }

  .section-title--with-status { display: flex; align-items: center; justify-content: space-between; }
  .status-badge {
    padding: 6px 14px;
    border-radius: 14px;
    font-size: 22px;
    line-height: 30px;
    font-weight: 500;
    white-space: nowrap;
  }
  .status-badge--not-submitted { color: #F04438; background: #FFF0F0; }
  .status-badge--loading { color: var(--primary-color); background: #EAF1FF; }
  .status-badge--submitted { color: #B26A00; background: #FFF2D9; }
  .status-badge--approved { color: #008A3E; background: #E1F7EA; }
  .status-badge--uploaded { color: #00A957; background: #E5F8ED; }
  .intro-text--rejected { background: #FCEBED; color: #667085; }
  .intro-text--rejected .intro-text__icon { background: #FFE1E3; color: #F04438; }
  .intro-text--rejected { padding: 20px 24px; border-radius: 16px; }

  .verification-card { display: flex; align-items: center; padding: 20px; }
  .verification-card__icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: #EEF3FF; color: var(--primary-color); font-size: 34px; flex-shrink: 0; }
  .verification-card__body { flex: 1; margin-left: 14px; min-width: 0; }
  .verification-card__title { font-size: 24px; line-height: 30px; font-weight: 600; color: var(--text-color-black); white-space: nowrap; }
  .verification-card__desc { margin-top: 6px; font-size: 22px; line-height: 30px; color: #666; }
  .verification-card__failure-desc { margin-top: 2px; color: #F04438; font-weight: 500; }
  .verification-card__action {
    margin-left: 12px;
    padding: 10px 16px;
    border: 2px solid var(--primary-color);
    border-radius: 16px;
    background: #fff;
    color: var(--primary-color);
    font-size: 22px;
    line-height: 30px;
    white-space: nowrap;
  }
  .verification-card__status { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; margin-left: 12px; }
  .verification-card__status .status-badge { font-size: 20px; }

  .document-tip {
    display: flex;
    align-items: flex-start;
    margin: 0 4px 16px;
    padding: 20px 24px;
    border-radius: 16px;
    background: #EAF1FF;
    color: #536B8A;
    font-size: 24px;
    line-height: 34px;
    white-space: pre-line;
  }

  .document-tip__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-right: 16px;
    border-radius: 50%;
    background: #DDEAFF;
    color: var(--primary-color);
    font-size: 26px;
    flex-shrink: 0;
  }

  .document-tip--rejected {
    background: #FCEBED;
  }

  .document-tip--rejected .document-tip__icon {
    background: #FFE1E3;
    color: #F04438;
  }

  .state-card {
    padding: 32px 28px;
    border-radius: 20px;
    background: #fff;
    color: #6F7788;
    font-size: 24px;
    line-height: 34px;
    text-align: center;
  }

  .state-card--error button {
    margin-left: 16px;
    padding: 8px 18px;
    border: 2px solid var(--primary-color);
    border-radius: 14px;
    background: #fff;
    color: var(--primary-color);
  }

  .card { padding: 20px; }
  .documents-card { padding-top: 8px; padding-bottom: 8px; }
  .doc-item { display: flex; align-items: center; min-height: 112px; padding: 18px 0; border-bottom: 1px solid #E8EDF5; }
  .doc-item:last-of-type { border-bottom: 0; }
  .doc-item.is-disabled { opacity: .58; }
  .doc-item__icon { width: 72px; height: 72px; display: flex; align-items: center; justify-content: center; background: #fff; border-radius: 12px; flex-shrink: 0; }
  .doc-item__icon img { max-width: 70%; max-height: 70%; object-fit: cover; }
  .doc-item__name { flex: 1; min-width: 0; margin-left: 20px; font-size: 27px; line-height: 36px; color: var(--text-color-black); word-break: break-word; }
  .doc-item__desc { margin-top: 6px; color: #A7ADBB; font-size: 23px; line-height: 32px; font-weight: 400; }
  .doc-item__status { display: flex; flex-direction: column; align-items: flex-end; justify-content: center; gap: 10px; margin-left: 12px; color: var(--primary-color); font-size: 30px; }
  .doc-item__status .status-badge { margin-right: 0; font-size: 20px; }
  .doc-item__status .upload-action { padding: 10px 18px; border: 2px solid var(--primary-color); border-radius: 16px; color: var(--primary-color); font-size: 24px; line-height: 30px; white-space: nowrap; }
  .doc-item__status .uploading-label { color: var(--primary-color); font-size: 22px; line-height: 30px; white-space: nowrap; }
  .doc-item__status .approved { color: #00C853; }
  .required-mark { margin-left: 6px; color: #F04438; font-weight: 600; }
  .file-input { display: none; }

  .review-tip { margin-top: 20px; }

  .post-review { margin-top: 20px; }
  .post-review__card { padding: 24px; border-radius: 20px; background: #fff; box-shadow: 0 4px 16px rgba(0, 0, 0, .06); }
  .post-review__card + .post-review__card { margin-top: 16px; }
  .post-review__title { color: var(--text-color-black); font-size: 26px; line-height: 36px; font-weight: 600; }
  .post-review__desc { margin-top: 8px; color: #6F7788; font-size: 23px; line-height: 34px; white-space: pre-line; }
  .post-review__card--action { display: flex; align-items: center; justify-content: space-between; }
  .post-review__card--action > div { flex: 1; min-width: 0; }
  .post-review__card--action > span { margin-left: 16px; padding: 10px 16px; border: 2px solid var(--primary-color); border-radius: 16px; color: var(--primary-color); font-size: 22px; white-space: nowrap; }

  &__footer { position: sticky; bottom: 0; z-index: 10; margin: 24px -24px -32px; padding: 20px 32px 28px; background: var(--bg-color); box-shadow: 0 -8px 24px rgba(58, 91, 151, .06); }
  .submit-button { display: block; width: 100%; padding: 0; border: 0; }
  .agreement { width: 100%; margin-bottom: 20px; font-size: 24px; line-height: 34px; color: #666; }
  .privacy-link { color: var(--link-color); }

  ::v-deep .agreement .van-checkbox { width: 100%; align-items: flex-start; }
  ::v-deep .agreement .van-checkbox__icon { margin-top: 4px; flex-shrink: 0; }
  ::v-deep .agreement .van-checkbox__label { flex: 1; min-width: 0; margin-left: 12px; line-height: 36px; white-space: normal; }

  ::v-deep .customer-service-dialog { border-radius: 20px; overflow: hidden; }
  .customer-service { position: relative; display: flex; flex-direction: column; background: #FFFFFF;
    &__close { position: absolute; right: 18px; top: 24px; width: 56px; height: 56px; z-index: 1;
      img { display: block; width: 32px; height: 32px; margin: 12px auto; }
    }
    &__title { padding: 50px 72px 20px 30px; color: var(--text-color-black); font-size: 42px; line-height: 48px; font-weight: 600; }
    &__content { flex: 1; padding: 28px 30px 0; }
    &__row { display: flex; align-items: center; justify-content: center; min-height: 150px; padding: 20px; background: #F8FAFE; border-radius: var(--border-radius-base); font-size: 28px; line-height: 40px;
      span { margin-right: 16px; color: var(--primary-color); flex-shrink: 0; }
      strong { color: var(--text-color-black); font-size: 32px; font-weight: 500; }
    }
    &__footer { padding: 30px; background: #FFFFFF; }
    &__copy { height: 80px; border-radius: 12px; font-size: 32px; line-height: 80px; }
  }

  .privacy-protection {
    display: flex;
    align-items: flex-start;
    margin-top: 18px;
    color: #6F7788;
    font-size: 22px;
    line-height: 30px;
  }

  .privacy-protection .van-icon {
    margin: 2px 12px 0 0;
    color: var(--primary-color);
    font-size: 28px;
    flex-shrink: 0;
  }

  .kyc-redirect-loading {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, .42);
  }

  ::v-deep .kyc-redirect-loading .van-loading__text {
    color: #fff;
    font-size: 26px;
    line-height: 36px;
  }
}
</style>
